"use client";

import { useState } from "react";
import CardNexusDeckImport from "@/components/CardNexusDeckImport";
import { importDeckListToSavedDeck } from "@/lib/deck-import";
import type { CardRecord } from "@/lib/types";
import type { SavedDeck } from "@/lib/local-store";

export default function DeckImportForm({
  cards,
  onImported,
  onCancel,
}: {
  cards: CardRecord[];
  onImported: (deck: SavedDeck) => void;
  onCancel?: () => void;
}) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [busy, setBusy] = useState(false);

  function submit() {
    const raw = code.trim();
    if (!raw) {
      setError("Colle une liste de deck.");
      return;
    }
    setBusy(true);
    setError("");
    setWarning("");
    try {
      const { deck, unresolved } = importDeckListToSavedDeck(raw, cards, name || undefined);
      if (unresolved.length > 0) {
        setWarning(
          `${unresolved.length} carte(s) non reconnue(s) ignorée(s): ${unresolved.slice(0, 5).join(", ")}${
            unresolved.length > 5 ? "…" : ""
          }`,
        );
      }
      setName("");
      setCode("");
      onImported(deck);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import impossible.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="glass rounded-none border border-white/5 p-3 sm:rounded-2xl sm:p-4">
      <div className="mb-4 border-b border-white/5 pb-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-cyan-400/80">Depuis CardNexus</p>
        <CardNexusDeckImport
          cards={cards}
          onImported={(deck, unresolved) => {
            setError("");
            setWarning(
              unresolved.length > 0
                ? `${unresolved.length} carte(s) non reconnue(s) ignorée(s): ${unresolved.slice(0, 5).join(", ")}${
                    unresolved.length > 5 ? "…" : ""
                  }`
                : "",
            );
            onImported(deck);
          }}
        />
      </div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">Ou coller une liste</p>
      <div className="grid gap-3 md:grid-cols-[220px_minmax(0,1fr)] lg:grid-cols-[260px_minmax(0,1fr)_auto]">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nom du deck (optionnel)"
          className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm"
        />
        <textarea
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={
            "Colle ici une liste, par ex.:\n1 Zed - Master of Shadows (VEN-143)\n5 Fury Rune (OGN-007)\n\nou bien\n\nLegend:\n1 Zed, Master of Shadows\n\nMainDeck:\n2 Death Mark"
          }
          rows={4}
          className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm md:row-span-2 lg:row-span-1"
        />
        <div className="flex gap-2 md:col-start-1 lg:col-auto lg:flex-col">
          <button
            type="button"
            onClick={submit}
            disabled={busy || cards.length === 0}
            className="flex-1 rounded-xl bg-gradient-to-r from-emerald-600 to-cyan-600 px-4 py-2.5 text-sm font-bold disabled:opacity-60 lg:flex-none"
          >
            {busy ? "Import…" : "Importer la liste"}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-xl border border-white/10 px-4 py-2.5 text-sm text-gray-400 hover:bg-white/5"
            >
              Fermer
            </button>
          )}
        </div>
      </div>
      {error && <p className="mt-3 text-sm text-red-300">{error}</p>}
      {warning && <p className="mt-3 text-sm text-amber-300">{warning}</p>}
      <p className="mt-3 text-xs text-gray-500">
        Formats acceptés: `quantité nom (CODE)` ou sections Legend / Champion / MainDeck / Battlefields /
        Runes / Sideboard. Le deck est ajouté à ta liste Decks (et Prepa).
      </p>
    </div>
  );
}
